import { forwardRef } from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { useLazyOptimizelyFeatureFlag } from '@zoopla/optimizely';
import { Box } from '@zoopla-eng/zds-components/box';
import {
    StyledError,
    StyledLabel,
    StyledLegend,
    StyledRadioButton
} from './EmailPreferencesToggle.styles';

interface EmailPreferencesToggleProps
    extends Omit<
        RadioGroup.RadioGroupItemProps,
        'value' | 'aria-describedby' | 'id' | 'onChange'
    > {
    name: string;
    legend: string;
    label: string;
    errorText?: string;
    onChange: RadioGroup.RadioGroupProps['onValueChange'];
}

export const EmailPreferencesToggle = forwardRef<
    HTMLButtonElement,
    EmailPreferencesToggleProps
>(({ legend, label, errorText, onChange, name, ...props }, ref) => {
    const [, isFeatureEnabled] = useLazyOptimizelyFeatureFlag(
        'client-crme-1822-preferences-buttons'
    );

    return (
        <>
            <RadioGroup.Root
                aria-labelledby="email-preferences-label"
                aria-describedby="email-preferences-legend"
                required
                name={name}
                onValueChange={onChange}
            >
                <Box pb="$S200">
                    <StyledLegend
                        id="email-preferences-legend"
                        data-testid="email-preferences-legend"
                    >
                        {legend}
                    </StyledLegend>
                </Box>
                <Box pb="$M100">
                    <StyledLabel
                        id="email-preferences-label"
                        data-testid="email-preferences-label"
                    >
                        {label}
                    </StyledLabel>
                </Box>
                <Box display="flex" columnGap="$S400">
                    <RadioGroup.Item
                        asChild
                        value="yes"
                        id="yes"
                        ref={ref}
                        aria-describedby="email-preferences-legend"
                        {...props}
                    >
                        <StyledRadioButton
                            variant={isFeatureEnabled ? 'unified' : 'yes'}
                            fw
                            category="secondary"
                            error={!!errorText}
                        >
                            Yes, please
                        </StyledRadioButton>
                    </RadioGroup.Item>
                    <RadioGroup.Item
                        asChild
                        value="no"
                        id="no"
                        ref={ref}
                        aria-describedby="email-preferences-legend"
                        {...props}
                    >
                        <StyledRadioButton
                            variant={isFeatureEnabled ? 'unified' : 'no'}
                            fw
                            category="secondary"
                            error={!!errorText}
                        >
                            No, thanks
                        </StyledRadioButton>
                    </RadioGroup.Item>
                </Box>
            </RadioGroup.Root>
            <div
                aria-live="assertive"
                data-testid="email-preferences-error-message"
            >
                {errorText && (
                    <Box pt="$S300">
                        <StyledError>{errorText}</StyledError>
                    </Box>
                )}
            </div>
        </>
    );
});
